import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { BagLogo } from "../../components/Logo";
import { toast } from "sonner";
import { ArrowRight, Check, PhoneOff, CalendarClock, Zap, BarChart3, Send } from "lucide-react";
import { track, attributionSnapshot } from "../../lib/analytics";

// First-party analytics only — never the applicant's email, phone or contact name.
const TRACK = (name, data) => track(name, data || {});

const BENEFITS = [
  { icon: PhoneOff, title: "Manje telefonskih poziva", text: "Gosti poručuju direktno preko Ponesi.rs." },
  { icon: CalendarClock, title: "Bolja organizacija", text: "Porudžbine i vreme preuzimanja na jednom mestu." },
  { icon: Zap, title: "Vista (Software)", text: "Brže upravljanje lokalnim porudžbinama i radom restorana." },
  { icon: BarChart3, title: "Analitika", text: "Pratite prodaju, proizvode i periode najveće i najmanje potražnje." },
];

const STEPS = [
  { n: "1", title: "Pošaljite prijavu", text: "Popunite kratku formu — treba vam manje od minuta." },
  { n: "2", title: "Kontaktiramo vas", text: "Javljamo se telefonom ili mejlom i dogovaramo detalje." },
  { n: "3", title: "Povezujemo vaš restoran", text: "Postavljamo meni i vaš restoran je vidljiv na Ponesi.rs." },
];

const FIELD = "w-full rounded-2xl border-2 border-slate-200 px-4 py-3.5 text-base bg-white outline-none focus:border-brand-red transition-colors";

function Field({ label, error, children, hint }) {
  return (
    <label className="block">
      <span className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-500">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && !error && <span className="block text-xs text-slate-400 mt-1">{hint}</span>}
      {error && <span className="block text-xs font-bold text-brand-red mt-1">{error}</span>}
    </label>
  );
}

function SuccessState({ applicationId }) {
  return (
    <div data-testid="b2b-success" className="bg-white rounded-3xl border-2 border-brand-green/30 p-7 text-center">
      <div className="w-14 h-14 rounded-full bg-brand-green/10 text-brand-green flex items-center justify-center mx-auto">
        <Check size={28} strokeWidth={3} />
      </div>
      <h3 className="font-b2b font-extrabold text-2xl mt-4">Upit je uspešno poslat!</h3>
      <p className="text-slate-600 mt-2">Hvala na interesovanju za Ponesi. Naš tim će vas kontaktirati uskoro.</p>
      {applicationId && (
        <p data-testid="b2b-application-id" className="text-xs font-black tracking-wider text-slate-400 mt-3">
          BROJ PRIJAVE: {applicationId}
        </p>
      )}
      <p className="text-sm font-bold text-brand-red mt-5">Poruči. Ponesi. Uživaj.</p>
    </div>
  );
}

export default function B2BLanding() {
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ city_id: "", restaurant_name: "", contact_name: "", email: "", phone: "", message: "", company_website: "" });
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const started = useRef(false);
  const clientId = useRef(crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
  const formRef = useRef(null);

  useEffect(() => {
    const TITLE = "Ponesi za restorane | Postanite partner";
    document.title = TITLE;
    // The app shell sets its own title on mount (parent effects run after children).
    const t = setTimeout(() => { document.title = TITLE; }, 0);
    const meta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
      return el;
    };
    meta("name", "description", "Pridružite svoj restoran Ponesi platformi. Omogućite gostima da poruče unapred i preuzmu bez čekanja.");
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://ponesi.rs/b2b";
    const font = document.createElement("link");
    font.rel = "stylesheet";
    font.href = "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&display=swap";
    document.head.appendChild(font);
    TRACK("b2b_page_view", { dedupe_key: "page" });
    return () => { clearTimeout(t); font.remove(); };
  }, []);

  useEffect(() => { api.get("/b2b/cities").then(({ data }) => setCities(data)).catch(() => setCities([])); }, []);

  const set = (k) => (e) => {
    if (!started.current) { started.current = true; TRACK("b2b_form_started", { dedupe_key: "form" }); }
    if (k === "city_id" && e.target.value) {
      TRACK("b2b_city_selected", { city_id: e.target.value, dedupe_key: `city:${e.target.value}` });
    }
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((x) => ({ ...x, [k]: null }));
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const validate = () => {
    const e = {};
    if (!form.city_id) e.city_id = "Izaberite grad.";
    if (form.restaurant_name.trim().length < 2) e.restaurant_name = "Unesite naziv restorana.";
    if (!/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(form.email.trim())) e.email = "Unesite ispravnu email adresu.";
    if (form.phone.replace(/\D/g, "").length < 8) e.phone = "Unesite kontakt telefon.";
    if (!consent) e.consent = "Potvrdite saglasnost za obradu podataka.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (sending || done) return;
    if (!validate()) {
      TRACK("b2b_application_error", { error_code: "VALIDATION_FAILED" });
      return;
    }
    setSending(true);
    TRACK("b2b_form_submitted", { city_id: form.city_id });
    try {
      const { data } = await api.post("/b2b/applications", {
        ...form, consent: true, client_application_id: clientId.current,
        attribution: attributionSnapshot(),
      });
      // b2b_application_success is emitted server-side (authoritative) on lead creation.
      setDone(data.application_id);
    } catch (err) {
      const d = err?.response?.data?.detail;
      TRACK("b2b_application_error", {
        error_code: (typeof d === "object" && d?.code) || (err?.response?.status ? `HTTP_${err.response.status}` : "NETWORK"),
      });
      toast.error((typeof d === "object" ? d.message : d) || "Došlo je do greške. Pokušajte ponovo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="b2b-page" className="font-b2b min-h-screen bg-white text-slate-900 antialiased">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-5 h-16 sm:h-20 flex items-center gap-3">
          <a href="/" data-testid="b2b-logo-link" className="flex items-center gap-2.5">
            <BagLogo size={48} className="w-11 h-11 sm:w-[52px] sm:h-[52px]" />
            <span className="font-extrabold text-lg sm:text-xl tracking-tight">Ponesi</span>
          </a>
          <span className="hidden sm:inline text-[11px] font-black uppercase tracking-[0.16em] text-brand-red bg-brand-red/8 rounded-full px-2.5 py-1">
            Za restorane
          </span>
          <button data-testid="b2b-header-cta" onClick={scrollToForm}
            className="ml-auto rounded-full bg-brand-red text-white px-4 sm:px-5 py-2.5 text-sm font-extrabold hover:bg-brand-redHover transition-colors">
            Postanite partner
          </button>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute -top-28 right-0 w-[260px] h-[260px] sm:w-[420px] sm:h-[420px] rounded-full bg-brand-red/5 blur-2xl" aria-hidden />
        <div className="absolute top-40 left-0 w-[220px] h-[220px] sm:w-[320px] sm:h-[320px] rounded-full bg-brand-green/5 blur-2xl" aria-hidden />
        <div className="relative max-w-5xl mx-auto px-5 pt-14 pb-16 sm:pt-24 sm:pb-24">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-brand-green">Ponesi partnerski program</p>
          <h1 className="mt-3 font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
            Olakšajte kupcima<br />i zaposlenima
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
            Pridružite svoj restoran Ponesi platformi i omogućite gostima da poruče unapred i preuzmu bez čekanja.
          </p>
          <p className="mt-2 text-sm font-semibold text-slate-400">
            Jednostavnije poručivanje. Manje poziva. Više organizovanih preuzimanja.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button data-testid="b2b-hero-cta" onClick={scrollToForm}
              className="group inline-flex items-center gap-2 rounded-2xl bg-brand-red text-white px-7 py-4 text-base font-extrabold hover:bg-brand-redHover active:scale-[0.98] transition-all">
              Postanite partner
              <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
            <span className="text-sm text-slate-400 font-semibold">Niš · Leskovac</span>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 pb-16 sm:pb-24">
        <h2 className="text-base md:text-lg font-extrabold uppercase tracking-[0.1em] text-slate-400">Zašto Ponesi</h2>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {BENEFITS.map(({ icon: Icon, title, text }) => (
            <div key={title} data-testid={`b2b-benefit-${title}`}
              className="rounded-3xl border-2 border-slate-100 bg-white p-6 hover:border-brand-red/25 transition-colors">
              <Icon size={22} className="text-brand-red" />
              <h3 className="mt-3.5 font-extrabold text-base md:text-lg uppercase tracking-wide">{title}</h3>
              <p className="mt-1.5 text-slate-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#FAFAF8] border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-5 py-16 sm:py-20">
          <h2 className="text-base md:text-lg font-extrabold uppercase tracking-[0.1em] text-slate-400">Kako funkcioniše</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-3xl bg-white border-2 border-slate-100 p-6">
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-full bg-brand-red text-white font-extrabold">{s.n}</span>
                <h3 className="mt-3.5 font-extrabold text-base md:text-lg">{s.title}</h3>
                <p className="mt-1.5 text-slate-600 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section ref={formRef} id="prijava" className="max-w-2xl mx-auto px-5 py-16 sm:py-24 scroll-mt-20">
        <h2 className="font-extrabold text-3xl sm:text-4xl tracking-tight">Prijavite svoj restoran</h2>
        <p className="mt-2 text-slate-600">Popunite formu i javljamo se u najkraćem roku.</p>

        <div className="mt-7">
          {done ? <SuccessState applicationId={done} /> : (
            <form data-testid="b2b-form" onSubmit={submit} noValidate className="space-y-4">
              <Field label="Grad *" error={errors.city_id}>
                <select data-testid="b2b-city" value={form.city_id} onChange={set("city_id")} className={FIELD}>
                  <option value="">Izaberite grad</option>
                  {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Naziv restorana *" error={errors.restaurant_name}>
                <input data-testid="b2b-restaurant-name" value={form.restaurant_name} onChange={set("restaurant_name")}
                  placeholder="npr. Restoran Central" className={FIELD} />
              </Field>
              <Field label="Email *" error={errors.email}>
                <input data-testid="b2b-email" type="email" inputMode="email" autoComplete="email"
                  value={form.email} onChange={set("email")} placeholder="restoran@example.com" className={FIELD} />
              </Field>
              <Field label="Kontakt telefon *" error={errors.phone} hint="0601234567 ili +381601234567">
                <input data-testid="b2b-phone" type="tel" inputMode="tel" autoComplete="tel"
                  value={form.phone} onChange={set("phone")} placeholder="0601234567" className={FIELD} />
              </Field>
              <Field label="Ime i prezime kontakt osobe">
                <input data-testid="b2b-contact-name" value={form.contact_name} onChange={set("contact_name")}
                  placeholder="Marko Marković" className={FIELD} />
              </Field>
              <Field label="Poruka">
                <textarea data-testid="b2b-message" value={form.message} onChange={set("message")} rows={4}
                  placeholder="Napišite nam dodatne informacije o restoranu..." className={`${FIELD} resize-y`} />
              </Field>

              {/* Honeypot: hidden from users, filled only by bots. */}
              <input tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.company_website}
                onChange={set("company_website")} name="company_website"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />

              <label className="flex gap-3 items-start bg-[#FAFAF8] rounded-2xl p-4 cursor-pointer">
                <input data-testid="b2b-consent" type="checkbox" checked={consent}
                  onChange={(e) => { setConsent(e.target.checked); setErrors((x) => ({ ...x, consent: null })); }}
                  className="mt-0.5 w-5 h-5 accent-brand-red shrink-0" />
                <span className="text-sm text-slate-600 leading-relaxed">
                  Slanjem forme potvrđujem da sam saglasan da Ponesi koristi navedene podatke radi kontakta u vezi sa
                  partnerskom prijavom.{" "}
                  <a href="/politika-privatnosti" target="_blank" rel="noreferrer" className="font-bold text-brand-red underline">
                    Politika privatnosti
                  </a>
                </span>
              </label>
              {errors.consent && <p data-testid="b2b-consent-error" className="text-xs font-bold text-brand-red">{errors.consent}</p>}

              <button data-testid="b2b-submit" type="submit" disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-brand-red text-white py-4 text-lg font-extrabold hover:bg-brand-redHover disabled:opacity-60 active:scale-[0.99] transition-all">
                <Send size={18} />{sending ? "Šaljemo..." : "POŠALJI UPIT"}
              </button>
            </form>
          )}
        </div>
      </section>

      <footer className="border-t border-slate-100">
        <div className="max-w-5xl mx-auto px-5 py-9 sm:py-10 flex flex-wrap items-center gap-x-6 gap-y-4">
          <BagLogo size={56} className="w-14 h-14 sm:w-16 sm:h-16" />
          <div className="mr-auto">
            <p className="font-extrabold">Ponesi</p>
            <p className="text-sm text-slate-400">Poruči. Ponesi. Uživaj.</p>
          </div>
          <a href="/politika-privatnosti" className="text-sm font-semibold text-slate-500 hover:text-brand-red">Politika privatnosti</a>
          <a href="/" className="text-sm font-semibold text-slate-500 hover:text-brand-red">Ponesi.rs</a>
        </div>
      </footer>
    </div>
  );
}
